/**
 * Compatibility adapter for product UI translations (M9).
 * Hardened thin t() layer — designed so i18next/react-intl could replace internals later.
 */
import { logMissingCatalogKey } from "./intlLogger.js";
import { FALLBACK_LOCALE } from "./localeResolution.js";

const loggedMissingKeys = new Set();
const loggedMissingParams = new Set();

function getNestedValue(catalog, keyPath) {
  const parts = keyPath.split(".");
  let current = catalog;
  for (const part of parts) {
    if (current == null || typeof current !== "object") {
      return null;
    }
    current = current[part];
  }
  return current;
}

function isPluralObject(value) {
  return (
    value != null &&
    typeof value === "object" &&
    !Array.isArray(value) &&
    ("one" in value || "other" in value)
  );
}

/**
 * Resolve plural form for es/en-style one/other catalogs.
 */
export function resolvePluralForm(pluralValue, count, locale) {
  if (!isPluralObject(pluralValue)) {
    return null;
  }
  const n = Number(count);
  if (!Number.isFinite(n)) {
    return pluralValue.other ?? pluralValue.one ?? null;
  }
  const language = String(locale || "").split("-")[0];
  if (language === "es" || language === "pt" || language === "fr") {
    return n === 1 ? pluralValue.one ?? pluralValue.other : pluralValue.other ?? pluralValue.one;
  }
  return n === 1 ? pluralValue.one ?? pluralValue.other : pluralValue.other ?? pluralValue.one;
}

function coerceParamValue(value) {
  if (value == null) {
    return "";
  }
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  if (typeof value === "string") {
    return value;
  }
  return String(value);
}

/**
 * Interpolate `{token}` placeholders safely as plain text (no HTML).
 */
export function interpolateTemplate(template, params = {}, { locale, key, devMode = false } = {}) {
  if (typeof template !== "string") {
    return String(template ?? "");
  }
  return template.replace(/\{(\w+)\}/g, (_, token) => {
    if (Object.prototype.hasOwnProperty.call(params, token)) {
      return coerceParamValue(params[token]);
    }
    if (devMode) {
      const logKey = `${locale}:${key}:${token}`;
      if (!loggedMissingParams.has(logKey)) {
        loggedMissingParams.add(logKey);
        console.warn(`[intl] Missing interpolation param "{${token}}" for key "${key}" (${locale})`);
      }
    }
    return `{${token}}`;
  });
}

function resolveRawValue(catalog, fallbackCatalog, key) {
  const primary = getNestedValue(catalog, key);
  if (primary != null) {
    return primary;
  }
  return getNestedValue(fallbackCatalog, key);
}

function logMissingKey(locale, key) {
  const logKey = `${locale}:${key}`;
  if (loggedMissingKeys.has(logKey)) {
    return;
  }
  loggedMissingKeys.add(logKey);
  logMissingCatalogKey(locale, key);
}

/**
 * @param {object} options
 * @param {object} options.catalog
 * @param {object} options.fallbackCatalog
 * @param {string} options.locale
 */
export function createTranslationAdapter({ catalog, fallbackCatalog, locale = FALLBACK_LOCALE }) {
  const devMode = process.env.NODE_ENV === "development";
  const missingKeys = [];

  function trackMissing(key, usedFallback) {
    if (locale !== FALLBACK_LOCALE && usedFallback) {
      missingKeys.push(key);
      logMissingKey(locale, key);
    }
  }

  function exists(key) {
    return resolveRawValue(catalog, fallbackCatalog, key) != null;
  }

  function getNamespace(namespace) {
    return {
      ...(fallbackCatalog?.[namespace] || {}),
      ...(catalog?.[namespace] || {}),
    };
  }

  function resolveMessage(key, params = {}) {
    const primary = getNestedValue(catalog, key);
    const fallback = getNestedValue(fallbackCatalog, key);
    const raw = primary ?? fallback;

    if (primary == null && fallback != null && locale !== FALLBACK_LOCALE) {
      trackMissing(key, true);
    }

    if (raw == null) {
      if (locale !== FALLBACK_LOCALE) {
        missingKeys.push(key);
      }
      logMissingKey(locale, `${key}:missing`);
      if (devMode) {
        return `[MISSING: ${key}]`;
      }
      return key;
    }

    let message = raw;
    if (isPluralObject(raw)) {
      const count = params.count ?? params.n;
      message = resolvePluralForm(raw, count, locale);
      if (message == null) {
        message = raw.other ?? raw.one ?? key;
      }
    }

    if (typeof message !== "string") {
      return String(message);
    }

    return interpolateTemplate(message, params, { locale, key, devMode });
  }

  function t(key, params) {
    return resolveMessage(key, params || {});
  }

  function getRaw(key) {
    const raw = resolveRawValue(catalog, fallbackCatalog, key);
    if (raw == null) {
      return null;
    }
    if (isPluralObject(raw)) {
      const count = 0;
      return resolvePluralForm(raw, count, locale) ?? raw.other ?? raw.one;
    }
    return typeof raw === "string" ? raw : String(raw);
  }

  function getMissingKeys() {
    return [...new Set(missingKeys)];
  }

  return {
    t,
    exists,
    getNamespace,
    getRaw,
    getMissingKeys,
    resolveMessage,
  };
}

export function resetTranslationAdapterStateForTests() {
  loggedMissingKeys.clear();
  loggedMissingParams.clear();
}
